// ============================================
// Manor Miser - UI Input Component
// ============================================
// Desktop keyboard input with 9-slice pixel art background
// and bitmap font text display.

import Phaser from 'phaser';

export class UIInput extends Phaser.GameObjects.Container {
  private bg: Phaser.GameObjects.NineSlice;
  private textDisplay: Phaser.GameObjects.BitmapText;
  private placeholderDisplay: Phaser.GameObjects.BitmapText;
  private placeholder: string;
  private value: string = '';
  private isFocused: boolean = false;
  private cursor: Phaser.GameObjects.Rectangle;
  private cursorBlink: Phaser.Time.TimerEvent | null = null;
  private maxLength: number = 16;
  private inputWidth: number;
  private inputHeight: number;
  private tintColor: number;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    width: number = 90,
    height: number = 14,
    placeholder: string = '',
    tint: number = 0xd4a855,
    inset: number = 5,
  ) {
    super(scene, x, y);

    this.placeholder = placeholder;
    this.inputWidth = width;
    this.inputHeight = height;
    this.tintColor = tint;

    // 9-slice background
    this.bg = scene.add.nineslice(0, 0, 'input-bg', undefined, width, height, inset, inset, inset, inset);
    this.bg.setTint(tint);
    this.add(this.bg);

    // Placeholder text (shown when empty and not focused)
    this.placeholderDisplay = scene.add.bitmapText(
      -width / 2 + 4, 0, 'pixel', placeholder.toUpperCase()
    );
    this.placeholderDisplay.setOrigin(0, 0.5);
    this.placeholderDisplay.setTint(0x666666);
    this.clampText(this.placeholderDisplay);
    this.add(this.placeholderDisplay);

    // Value text
    this.textDisplay = scene.add.bitmapText(-width / 2 + 4, 0, 'pixel', '');
    this.textDisplay.setOrigin(0, 0.5);
    this.textDisplay.setTint(0xf5e6d3);
    this.add(this.textDisplay);

    // Cursor
    this.cursor = scene.add.rectangle(0, 0, 1, height - 4, 0xf5e6d3);
    this.cursor.setOrigin(0, 0.5);
    this.cursor.setVisible(false);
    this.add(this.cursor);

    // Interactive
    this.setSize(width, height);
    this.setInteractive({ useHandCursor: true });

    this.on('pointerdown', () => {
      this.focus();
    });

    // Pre-compute a brighter hover tint
    const r = Math.min(255, ((tint >> 16) & 0xFF) + 20);
    const g = Math.min(255, ((tint >> 8) & 0xFF) + 20);
    const b = Math.min(255, (tint & 0xFF) + 20);
    const hoverTint = (r << 16) | (g << 8) | b;

    this.on('pointerover', () => {
      if (!this.isFocused) {
        this.bg.setTint(hoverTint);
      }
    });

    this.on('pointerout', () => {
      if (!this.isFocused) {
        this.bg.setTint(tint);
      }
    });

    // Click outside to blur
    scene.input.on('pointerdown', (_pointer: Phaser.Input.Pointer, targets: Phaser.GameObjects.GameObject[]) => {
      if (this.isFocused && !targets.includes(this)) {
        this.blur();
      }
    });

    // Clean up keyboard listener on scene shutdown
    scene.events.on('shutdown', () => {
      this.blur();
    });

    scene.add.existing(this);
    this.updateDisplay();
  }

  private clampText(text: Phaser.GameObjects.BitmapText): void {
    text.setScale(1);
    const maxW = this.inputWidth - 8;
    if (text.width > maxW) {
      text.setScale(maxW / text.width);
    }
  }

  private updateDisplay(): void {
    if (this.value) {
      this.textDisplay.setText(this.value.toUpperCase());
      this.clampText(this.textDisplay);
      this.placeholderDisplay.setVisible(false);
    } else {
      this.textDisplay.setText('');
      this.placeholderDisplay.setVisible(!this.isFocused);
    }

    // Update cursor position
    const textW = this.textDisplay.width * this.textDisplay.scaleX;
    this.cursor.setX(-this.inputWidth / 2 + 4 + textW + 1);
  }

  focus(): void {
    if (this.isFocused) return;

    this.isFocused = true;
    this.bg.setTint(0xffffff);
    this.placeholderDisplay.setVisible(false);

    // Listen for keyboard
    this.scene.input.keyboard?.on('keydown', this.handleKeyDown, this);

    // Blinking cursor
    this.cursor.setVisible(true);
    this.cursorBlink = this.scene.time.addEvent({
      delay: 530,
      callback: () => { this.cursor.setVisible(!this.cursor.visible); },
      loop: true,
    });

    this.updateDisplay();
  }

  private handleKeyDown = (event: KeyboardEvent): void => {
    if (!this.isFocused) return;
    event.preventDefault();

    if (event.key === 'Backspace') {
      this.value = this.value.slice(0, -1);
    } else if (event.key === 'Enter' || event.key === 'Tab') {
      this.blur();
      return;
    } else if (event.key === 'Escape') {
      this.blur();
      return;
    } else if (event.key.length === 1 && this.value.length < this.maxLength) {
      this.value += event.key;
    }

    this.updateDisplay();
  };

  blur(): void {
    if (!this.isFocused) return;

    this.isFocused = false;
    this.bg.setTint(this.tintColor);
    this.cursor.setVisible(false);

    this.scene.input.keyboard?.off('keydown', this.handleKeyDown, this);

    if (this.cursorBlink) {
      this.cursorBlink.destroy();
      this.cursorBlink = null;
    }

    this.updateDisplay();
  }

  getValue(): string {
    return this.value;
  }

  setValue(value: string): void {
    this.value = value.slice(0, this.maxLength);
    this.updateDisplay();
  }

  setMaxLength(length: number): void {
    this.maxLength = length;
  }

  clear(): void {
    this.value = '';
    this.updateDisplay();
  }
}
